// 逐級大獎跑分計劃 — 對應 Unity WinCelebrationUI.RunProgress 的逐級 escalation。
//
// Unity 報獎從 Big 級起連續跑分，每跨越一個門檻（10/25/50/100×bet）就升到下一級並換動畫，
// 跑到 winTierFor(totalWin) 對應級別為止。例：60×bet 會演 Big→Mega→Super 三段後停在 Super。
// 本函式把這段拆成「每級一段」的資料，view 層據此依序播 Spine 動畫（{tier}Win{variant}_in→_loop）並跑分。

import { WIN_TIER_RATIOS, type WinTier } from '../../constants'

/** 大獎跑分分級（排除未達門檻的 none 與封頂的 max；max 另走最高贏分 Spine）。 */
export type CelebrationTier = 'big' | 'mega' | 'super' | 'epic'

/** 由低到高的分級序，動畫基名即 `${tier}Win${variant}`（例 bigWin1）。 */
const TIER_ORDER: readonly CelebrationTier[] = ['big', 'mega', 'super', 'epic']

export interface WinCelebrationSegment {
  /** 本段分級。 */
  readonly tier: CelebrationTier
  /** Spine 動畫基名（不含 _in/_loop 後綴），例 'bigWin1'。 */
  readonly animation: string
  /** 本段跑分起始金額（big 段自 0 起；其餘自該級門檻起）。 */
  readonly from: number
  /** 本段跑分結束金額（中間級為下一級門檻，最後一級為 totalWin）。 */
  readonly to: number
}

function ratioOf(tier: CelebrationTier): number {
  return WIN_TIER_RATIOS.find((entry) => entry.tier === tier)?.ratio ?? 0
}

/** 目標分級在 TIER_ORDER 的索引；max 視為 epic（跑到 totalWin），none 回 -1。 */
function targetIndex(tier: WinTier): number {
  if (tier === 'max') return TIER_ORDER.length - 1
  return TIER_ORDER.indexOf(tier as CelebrationTier)
}

/**
 * 建立逐級大獎跑分計劃。
 * @param totalWin 本次報獎總贏分（顯示單位）。
 * @param totalBet 本局總下注（分級門檻分母）。
 * @param variant 動畫變體：1=主遊戲（xxxWin1）、2=免費遊戲（xxxWin2）。
 * @param tier    已算好的目標分級（預設由 winTierFor 推導）；傳入可讓呼叫端與音效分級一致。
 * @returns 由 big 到目標級的分段陣列；未達 big（<10×bet）回空陣列（不報獎）。
 */
export function buildWinCelebrationPlan(
  totalWin: number,
  totalBet: number,
  variant: 1 | 2,
  tier: WinTier,
): readonly WinCelebrationSegment[] {
  const target = targetIndex(tier)
  if (target < 0 || totalBet <= 0 || totalWin <= 0) return []

  const segments: WinCelebrationSegment[] = []
  for (let i = 0; i <= target; i++) {
    const segTier = TIER_ORDER[i]!
    const from = i === 0 ? 0 : ratioOf(segTier) * totalBet
    const to = i === target ? totalWin : ratioOf(TIER_ORDER[i + 1]!) * totalBet
    segments.push({ tier: segTier, animation: `${segTier}Win${variant}`, from, to })
  }
  return segments
}
